/* eslint-disable react-native/no-color-literals */
/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  View,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import { formatISO, startOfDay, endOfDay, addMonths } from 'date-fns';
import { isWeb, isNative, isAndroid, isIos } from '@global/screens';
import Colors from '@global/colors';
import {
  parseDate,
  changeTimeZone,
  getRandomColor,
  hasNoAccess,
  exportCsvApi,
  exportPdf,
} from '@global/helpers';
import Toast from 'react-native-toast-message';
import Image from '@components/global/Image';
import Button from '@components/form/Button';
import CheckBox from '@components/form/CheckBox';
import DatePicker from '@components/form/DatePicker';
import SociollaText from '@components/sociolla/SociollaText';
import ScrollContainer from '@components/global/ScrollContainer';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import SalesPersonSearch from '@components/form/search/SalesPersonSearch';
import ActivityCalendar from '@/screens/calendar/components/ActivityCalendar';
import ActivityItem from '@/screens/calendar/components/ActivityItem';
import SalesActivityDetail from '@/screens/sales-activity/components/SalesActivityDetail';
import * as Layout from '@components/screen/Layout';
import ModalFull from '@components/modal/ModalFull';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';

const dimension = Dimensions.get('screen');
const limit = 10;
const filterUsersPageSize = 50;
const filterUsersMaxPages = 40;

const CalendarScreen = (props) => {
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();
  const activityRef = useRef();
  // 55 is the app header height, present on every platform.
  const totalHeight = dimension.height - insets.top - insets.bottom - 55;
  const today = new Date();
  const resetCalendar = isWeb
    ? [today, new Date(today.getFullYear(), today.getMonth() + 1, 1)]
    : [today]; // web have two month calendar
  const userData = useSelector((state) => state.auth.user);
  const menuPermission = useSelector((state) => state.auth.menuPermission);
  const hasInvoiceCollectionExportAccess = Boolean(
    menuPermission?.includes('InvoiceCollectionExport'),
  );
  const userRole = userData && userData.role && userData.role.code;

  const [isReady, setIsReady] = useState(false);
  const [isFirst, setIsFirst] = useState(true);
  const [isHideMobile, setIsHideMobile] = useState(false);
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState({});
  const [loadingMore, setLoadingMore] = useState({});
  const [moreEmpty, setMoreEmpty] = useState({});
  const [showActivityDetail, setShowActivityDetail] = useState('');
  const [filterList, setFilterList] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState([]);
  const [calendarHeight, setCalendarHeight] = useState(0);
  const [calendar, setCalendar] = useState(resetCalendar);
  const [calendarSelected, setCalendarSelected] = useState(today);
  const [tempCalendarResponse, setTempCalendarResponse] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [activityObj, setActivityObj] = useState({});
  const [activityUser, setActivityUser] = useState({});
  const [activityTotal, setActivityTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [filterSalesTeamChanged, setFilterSalesTeamChanged] = useState(false);
  const exportCalenderActivity = useSelector(
    (state) => state.modal.exportCalenderActivity,
  );
  const exportInvoiceCollection = useSelector(
    (state) => state.modal.exportInvoiceCollection,
  );
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportingInvoiceCollection, setExportingInvoiceCollection] =
    useState(false);
  const [filterInvoiceExport, setFilterInvoiceExport] = useState({
    sales_team: [],
    salesman: null,
    visited_at: {
      $gte: '',
      $lte: '',
    },
  });

  const [filterExport, setFilterExport] = useState({
    sales_team: [],
    sales_person: [],
    beauty_advisor: [],
    merchandiser: [],
    promotor: [],
    collector: [],
    visited_at: {
      $gte: '',
      $lte: '',
    },
  });

  const onSetFilter = (val) => {
    const temp = JSON.parse(JSON.stringify(selectedFilter));
    const idx = selectedFilter.indexOf(val);
    if (idx > -1) {
      temp.splice(idx, 1);
    } else {
      temp.push(val);
    }
    setSelectedFilter(temp);
  };

  // if go to previous month
  const onPrev = () => {
    if (!loading) {
      if (isWeb) {
        const first = calendar[0];
        const last = calendar[1];
        setCalendar([
          new Date(first.getFullYear(), first.getMonth() - 1, first.getDate()),
          new Date(last.getFullYear(), last.getMonth() - 1, last.getDate()),
        ]);
      } else {
        const first = calendar[0];
        setCalendar([
          new Date(first.getFullYear(), first.getMonth() - 1, first.getDate()),
        ]);
      }
    }
  };

  // if go to next month
  const onNext = () => {
    if (!loading) {
      if (isWeb) {
        const first = calendar[0];
        const last = calendar[1];
        setCalendar([
          new Date(first.getFullYear(), first.getMonth() + 1, first.getDate()),
          new Date(last.getFullYear(), last.getMonth() + 1, last.getDate()),
        ]);
      } else {
        const first = calendar[0];
        setCalendar([
          new Date(first.getFullYear(), first.getMonth() + 1, first.getDate()),
        ]);
      }
    }
  };

  const resetData = () => {
    setLoading(true);
    setSkip(0);
    setLoadingMore(false);
    setMoreEmpty(false);
    setTempCalendarResponse([]);
    setActivityTotal(0);
    setActivityUser({});
    setActivityObj({});
    setActivityData([]);
    setSelectedFilter([]);
    setIsFirst(true);
  };

  useEffect(() => {
    let mounted = true;
    if (mounted && isReady) {
      onChangeCalender();
    }
    return () => {
      mounted = false;
    };
  }, [calendar]);

  useEffect(() => {
    let mounted = true;
    if (mounted && !isWeb) {
      setCalendarHeight(isHideMobile ? 145 : 460);
    }
    return () => {
      mounted = false;
    };
  }, [isHideMobile]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      convertFinalCalendar();
    }
    return () => {
      mounted = false;
    };
  }, [tempCalendarResponse]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setSelectedFilter([]);
      doSearch();
      fetchFilterUsers();
    }
    return () => {
      mounted = false;
    };
  }, [calendarSelected]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setFilterSalesTeamChanged(true);
      setTimeout(() => {
        setFilterSalesTeamChanged(false);
      }, 500);
    }
    return () => {
      mounted = false;
    };
  }, [filterExport.sales_team]);

  const getActivities = useApi(MsB2b.getActivities);

  const onChangeCalender = async () => {
    setSkip(0);
    setLoadingMore(false);
    setMoreEmpty(false);
    doSearch();
  };

  // convert calendar value
  const convertFinalCalendar = () => {
    // setIsCalendarLoading(true);
    if (tempCalendarResponse && tempCalendarResponse.length) {
      const obj = {};
      const objUser = {};
      const emptyBefore = {};
      let itemOffset = 0;
      const itemOffsetData = {};
      const checkDate = (val) => {
        const d = new Date(val);
        return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      };
      for (let i = 0; i < calendar.length; i++) {
        const item_2 = calendar[i];
        const c_month = item_2.getMonth();
        const c_month_name = parseDate(item_2, 'LLLL');
        const c_year = item_2.getFullYear();
        const c_last_date_full = new Date(c_year, c_month + 1, 0);
        const c_last_date = c_last_date_full.getDate();
        emptyBefore[c_month] = [];
        for (let date = 0; date < c_last_date; date++) {
          const c_date = date + 1;
          const current_date = parseDate(
            new Date(c_year, c_month, c_date),
            'yyyy-LL-dd',
          );
          const match = tempCalendarResponse.filter(
            (a) =>
              (a.visited_at ? new Date(a.visited_at).getDate() : -1) ==
                c_date &&
              (a.visited_at ? new Date(a.visited_at).getMonth() : -1) ==
                c_month,
          );
          // const handleEmpty = () => {
          //   if (emptyBefore[c_month] && emptyBefore[c_month].length) {
          //     let finalEmpty = [];
          //     let finalKey = current_date;
          //     itemOffset += 40;
          //     if (emptyBefore[c_month].length > 1) {
          //       const f_date = new Date(emptyBefore[c_month][0]);
          //       const l_date = new Date(
          //         emptyBefore[c_month][emptyBefore[c_month].length - 1],
          //       );
          //       finalEmpty = [{ isEmpty: true }];
          //       finalKey = `${f_date.getDate()}-${l_date.getDate()} ${parseDate(
          //         f_date,
          //         'LLL',
          //       )}`;
          //     } else {
          //       finalEmpty = [];
          //       finalKey = emptyBefore[c_month][0];
          //     }
          //     if (obj[c_month_name]) {
          //       obj[c_month_name][finalKey] = finalEmpty;
          //     } else {
          //       obj[c_month_name] = {
          //         [finalKey]: finalEmpty,
          //       };
          //     }
          //     addOffset(finalKey);
          //     emptyBefore[c_month] = [];
          //   }
          // };
          const addOffset = async (name) => {
            itemOffsetData[name] = itemOffset;
          };
          if (match && match.length) {
            // handleEmpty();
            for (let k = 0; k < match.length; k++) {
              const item = match[k];
              const cust = item.user && item.user.id ? item.user.id : '-';
              if (!objUser[cust]) {
                objUser[cust] = getRandomColor();
              }
            }
            if (obj[c_month_name]) {
              obj[c_month_name][current_date] = match;
            } else {
              obj[c_month_name] = {
                [current_date]: match,
              };
            }
            itemOffset += 55;
            addOffset(current_date);
          } else {
            if (checkDate(calendarSelected) == checkDate(current_date)) {
              // handleEmpty();
              if (obj[c_month_name]) {
                obj[c_month_name][current_date] = [];
              } else {
                obj[c_month_name] = {
                  [current_date]: [],
                };
              }
              itemOffset += 40;
              addOffset(current_date);
            } else {
              emptyBefore[c_month].push(current_date);
            }
            if (new Date(current_date).getDate() == c_last_date) {
              // handleEmpty();
            }
          }
        }
      }
      setActivityUser(objUser);
      setActivityObj(obj);
      setActivityData(Object.keys(obj));
      if (obj && Object.keys(obj).length) {
        // const result_length = {};
        // const keys = Object.keys(obj);
        // for (let i = 0; i < keys.length; i++) {
        //   const key = keys[i];
        //   if (obj[key] && Object.keys(obj[key]).length) {
        //     const childKeys = Object.keys(obj[key]);
        //     for (let j = 0; j < childKeys.length; j++) {
        //       const childKey = childKeys[j];
        //       const total =
        //         obj[key][childKey]?.filter((ac) => !ac.isEmpty)?.length || 0;
        //       if (result_length[key]) {
        //         result_length[key] += total;
        //       } else {
        //         result_length[key] = total;
        //       }
        //     }
        //   }
        // }
        // setActivityTotal(result_length);
        let result_length = 0;
        const keys = Object.keys(obj);
        for (const key of keys) {
          if (obj[key] && Object.keys(obj[key]).length) {
            const childKeys = Object.keys(obj[key]);
            for (const childKey of childKeys) {
              const total =
                obj[key][childKey]?.filter((ac) => !ac.isEmpty)?.length || 0;
              result_length += total;
            }
          }
        }
        setActivityTotal(result_length);
      }
      setTimeout(() => {
        setIsCalendarLoading(false);
        if (!isFirst) {
          let offset = 0;
          if (itemOffsetData && Object.keys(itemOffsetData).length) {
            const keys = Object.keys(itemOffsetData);
            const findIndex = keys.findIndex(
              (d) => d == parseDate(calendarSelected, 'yyyy-LL-dd'),
            );
            if (findIndex > 0) {
              offset = itemOffsetData[keys[findIndex]];
            }
          }
          if (skip == 0) {
            setTimeout(() => {
              try {
                activityRef.current.scrollToOffset({
                  animated: true,
                  offset: offset,
                });
              } catch (error) {}
            }, 250);
          }
        }
        setIsFirst(false);
      }, 250);
    } else {
      setTimeout(() => {
        setIsCalendarLoading(false);
      }, 250);
    }
  };

  // on search calendar callback
  const doSearch = async (isReset = false, newSkip = 0) => {
    if (newSkip > 0) {
      setSkip(newSkip);
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      let filter = {};

      // if (calendar && calendar.length) {
      //   if (calendar.length == 1) {
      //     if (index == -1) {
      //       setSkip({ ...skip, 'skip-0': 0 });
      //       setLoadingMore({ ...loadingMore, 'loadingMore-0': false });
      //     }

      //     if (index > -1) {
      //       const month = calendar[index].getMonth() + 1;
      //       filter = {
      //         visited_at: {
      //           $gte: new Date(
      //             calendar[index].getFullYear(),
      //             calendar[index].getMonth(),
      //             1,
      //           ).toISOString(),
      //           $lte: new Date(
      //             calendar[index].getFullYear(),
      //             month,
      //             1,
      //           ).toISOString(),
      //         },
      //       };
      //     } else {
      //       const month = calendar[0].getMonth() + 1;
      //       filter = {
      //         visited_at: {
      //           $gte: new Date(
      //             calendar[0].getFullYear(),
      //             calendar[0].getMonth(),
      //             1,
      //           ).toISOString(),
      //           $lte: new Date(
      //             calendar[0].getFullYear(),
      //             month,
      //             1,
      //           ).toISOString(),
      //         },
      //       };
      //     }
      //   } else {
      //     if (index == -1) {
      //       setSkip({ ...skip, 'skip-0': 0, 'skip-1': 0 });
      //       setLoadingMore({
      //         ...loadingMore,
      //         'loadingMore-0': false,
      //         'loadingMore-1': false,
      //       });
      //     }
      //     if (index > -1) {
      //       const month = calendar[index].getMonth() + 1;
      //       filter = {
      //         visited_at: {
      //           $gte: new Date(
      //             calendar[index].getFullYear(),
      //             calendar[index].getMonth(),
      //             1,
      //           ).toISOString(),
      //           $lte: new Date(
      //             calendar[index].getFullYear(),
      //             month,
      //             1,
      //           ).toISOString(),
      //         },
      //       };
      //     } else {
      //       const month = calendar[1].getMonth() + 1;
      //       filter = {
      //         visited_at: {
      //           $gte: new Date(
      //             calendar[0].getFullYear(),
      //             calendar[0].getMonth(),
      //             1,
      //           ).toISOString(),
      //           $lte: new Date(
      //             calendar[1].getFullYear(),
      //             month,
      //             1,
      //           ).toISOString(),
      //         },
      //       };
      //     }
      //   }
      // }

      if (calendarSelected) {
        filter = {
          type: 'calendar',
          visited_at: {
            $gte: formatISO(startOfDay(changeTimeZone(calendarSelected))),
            $lte: formatISO(endOfDay(changeTimeZone(calendarSelected))),
          },
        };
      }

      if (!isReset && selectedFilter?.length) {
        filter['user.id'] = { $in: selectedFilter };
      }

      const filterString = JSON.stringify(filter);

      const response = await getActivities.request({
        params: { filter: filterString, skip: newSkip, limit: limit },
      });

      if (response?.data?.data?.length) {
        const new_response = response.data.data.map((item) => {
          if (item._id) {
            item.id = item._id;
          }
          return item;
        });

        if (newSkip > 0) {
          const old = JSON.parse(JSON.stringify(tempCalendarResponse));
          const result = old.concat(new_response);
          setTempCalendarResponse(result);
        } else {
          setTempCalendarResponse(new_response);
        }
      } else {
        if (newSkip > 0) {
          setMoreEmpty(true);
        } else {
          resetData();
        }
      }
      setIsReady(true);
      if (newSkip > 0) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.log('e', e);
      setIsReady(true);
      if (newSkip > 0) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const getFilterUsers = useApi(MsB2b.getActivities);

  const fetchFilterUsers = async () => {
    if (!calendarSelected) {
      return;
    }
    try {
      const filter = {
        type: 'calendar',
        visited_at: {
          $gte: formatISO(startOfDay(changeTimeZone(calendarSelected))),
          $lte: formatISO(endOfDay(changeTimeZone(calendarSelected))),
        },
      };
      const filterString = JSON.stringify(filter);
      const objUser = {};
      const listFilter = [];
      let skip = 0;
      let page = 0;
      while (page < filterUsersMaxPages) {
        const response = await getFilterUsers.request({
          params: {
            filter: filterString,
            fields: 'user',
            skip,
            limit: filterUsersPageSize,
          },
        });
        const docs = response?.data?.data || [];
        docs.forEach((item) => {
          const cust = item.user && item.user.id ? item.user.id : '-';
          if (cust != '-' && !objUser[cust]) {
            objUser[cust] = getRandomColor();
            listFilter.push({
              id: cust,
              name: item.user.name,
              color: objUser[cust],
            });
          }
        });
        page += 1;
        if (docs.length < filterUsersPageSize) {
          break;
        }
        skip += filterUsersPageSize;
      }
      setFilterList(listFilter);
    } catch (e) {
      console.log('e', e);
    }
  };

  const SALESTEAM_FILTER = useMemo(() => {
    const filter = {};
    const userSalesTeam =
      Array.isArray(userData?.sales_team) && userData.sales_team.length
        ? userData.sales_team.map((a) => a.id)
        : [];
    if (
      ![
        'superadmin',
        'distributor_manager',
        'general_manager',
        'national_key_account_manager',
        'tax_support',
        'vice_president',
        'general',
        'regional_manager',
        'group_key_account_manager',
        'regional_support',
        'key_account_manager',
      ].includes(userRole)
    ) {
      if (
        userSalesTeam.length &&
        !hasNoAccess([
          'branch_head',
          'salesman',
          'sales_supervisor',
          'regional_manager',
          'regional_support',
          'key_account_manager',
          'group_key_account_manager',
          'sales_admin',
          'beauty_advisor',
          'merchandiser',
          'regional_distributor_manager',
          'collector',
        ])
      ) {
        filter['_id'] = { $in: userSalesTeam };
      } else {
        filter['empty'] = true;
      }
    }
    return filter;
  }, [userData, userRole]);

  const SALESPERSON_FILTER = useMemo(
    () =>
      (role = 'salesman', onlyfilter = false) => {
        const filter = {};
        let userSalesTeam =
          Array.isArray(userData?.sales_team) && userData.sales_team.length
            ? userData.sales_team.map((a) => a.id)
            : [];
        if (exportCalenderActivity) {
          userSalesTeam =
            Array.isArray(filterExport?.sales_team) &&
            filterExport.sales_team.length
              ? filterExport.sales_team.map((a) => a.id)
              : [];
        }
        if (userSalesTeam?.length) {
          if (exportCalenderActivity || userRole == 'sales_supervisor') {
            if (userSalesTeam[0] !== 'all') {
              filter.sales_team = {
                $elemMatch: { id: { $in: userSalesTeam } },
              };
            } else {
              filter['role.code'] = role;
              if (role === 'salesman') {
                filter.sales_code = { $exists: true };
              }
              filter.remove_default_filter = true;
            }
            if (role != 'salesman') {
              filter['role.code'] = role;
              filter.remove_default_filter = true;
            }
          } else if (
            ['branch_head', 'finance_supervisor', 'sales_admin'].includes(
              userRole,
            )
          ) {
            filter.remove_default_filter = true;
            if (role != 'salesman') {
              filter['role.code'] = role;
              if (userSalesTeam[0] !== 'all') {
                filter.sales_team = {
                  $elemMatch: { id: { $in: userSalesTeam } },
                };
              } else {
                filter['role.code'] = role;
                filter.sales_code = { $exists: true };
                filter.remove_default_filter = true;
              }
            } else {
              filter['$and'] = [
                {
                  $or: [
                    { 'role.code': 'sales_supervisor' },
                    {
                      $and: [
                        { 'role.code': 'salesman' },
                        { sales_code: { $exists: true } },
                      ],
                    },
                  ],
                },
                { sales_team: { $elemMatch: { id: { $in: userSalesTeam } } } },
              ];
            }
          }
        } else if (
          !exportCalenderActivity
            ? !['superadmin', 'general', 'general_manager'].includes(userRole)
            : true
        ) {
          filter['empty'] = true;
        }

        if (
          onlyfilter &&
          ['beauty_advisor', 'merchandiser', 'promotor'].includes(role)
        ) {
          filter.remove_default_filter = true;
          filter['role.code'] = role;
        }
        return filter;
      },
    [userData, userRole, exportCalenderActivity, filterExport],
  );

  // [B2B-5745] Sales person filter for the Invoice Collection PDF modal — scoped
  // to filterInvoiceExport.sales_team (this modal's own state). Invoice
  // collection applies to salesman and collector roles.
  const INVOICE_SALESPERSON_FILTER = useMemo(() => {
    const filter = {
      'role.code': { $in: ['salesman', 'collector'] },
      remove_default_filter: true,
    };
    let userSalesTeam =
      Array.isArray(userData?.sales_team) && userData.sales_team.length
        ? userData.sales_team.map((a) => a.id)
        : [];
    if (
      Array.isArray(filterInvoiceExport.sales_team) &&
      filterInvoiceExport.sales_team.length
    ) {
      userSalesTeam = filterInvoiceExport.sales_team.map((a) => a.id);
    }
    if (userSalesTeam?.length) {
      if (userSalesTeam[0] !== 'all') {
        filter.sales_team = { $elemMatch: { id: { $in: userSalesTeam } } };
      } else {
        filter.sales_code = { $exists: true };
        filter.remove_default_filter = true;
      }
    } else if (
      !['superadmin', 'general', 'general_manager'].includes(userRole)
    ) {
      filter['empty'] = true;
    }
    return filter;
  }, [userData, userRole, filterInvoiceExport]);

  const onExportInvoiceCollection = async () => {
    setExportingInvoiceCollection(true);
    try {
      const salesmanId = ['salesman', 'collector'].includes(userRole)
        ? userData?._id
        : filterInvoiceExport.salesman?.id;

      if (!salesmanId) {
        if (isIos) {
          Toast.show({
            text1: 'Salesman filter must be filled',
            type: 'error',
          });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Salesman filter must be filled',
              isConfirm: false,
            }),
          );
        }
        setExportingInvoiceCollection(false);
        return;
      }

      if (
        !filterInvoiceExport.visited_at?.$gte ||
        !filterInvoiceExport.visited_at?.$lte
      ) {
        if (isIos) {
          Toast.show({ text1: 'Date filter must be filled', type: 'error' });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Date filter must be filled',
              isConfirm: false,
            }),
          );
        }
        setExportingInvoiceCollection(false);
        return;
      }

      await exportPdf(
        'invoice-collection',
        JSON.stringify({
          salesman_id: salesmanId,
          date_gte: filterInvoiceExport.visited_at.$gte,
          date_lte: filterInvoiceExport.visited_at.$lte,
        }),
      );

      setFilterInvoiceExport({
        sales_team: [],
        salesman: null,
        visited_at: { $gte: '', $lte: '' },
      });
      dispatch(showPopup({ name: 'exportInvoiceCollection', value: false }));
    } catch (error) {
      if (isIos) {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      } else {
        dispatch(showPopup({ name: 'exportInvoiceCollection', value: false }));
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description:
              error?.response?.data?.message || 'A system error occurred',
            isConfirm: false,
          }),
        );
      }
    }
    setExportingInvoiceCollection(false);
  };

  const onExport = async () => {
    setExporting(true);
    const userDetail = userData?.role;
    const userDetailRole = userData?.role?.code;
    try {
      if (
        !filterExport.sales_person?.length &&
        !filterExport.beauty_advisor?.length &&
        !filterExport.merchandiser?.length &&
        !filterExport.promotor?.length &&
        !filterExport.collector?.length &&
        ![
          'salesman',
          'beauty_advisor',
          'merchandiser',
          'promotor',
          'collector',
        ].includes(userRole)
      ) {
        if (isIos) {
          Toast.show({
            text1: 'Sales person filter must be filled',
            type: 'error',
          });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Sales person filter must be filled',
              isConfirm: false,
            }),
          );
        }
        setExporting(false);
        return;
      }
      if (!filterExport.visited_at?.$gte || !filterExport.visited_at?.$lte) {
        if (isIos) {
          Toast.show({ text1: 'Date filter must be filled', type: 'error' });
        } else {
          dispatch(
            showAlert({
              show: true,
              title: 'Error',
              description: 'Date filter must be filled',
              isConfirm: false,
            }),
          );
        }
        setExporting(false);
        return;
      }

      let filterRole = null;
      let filterRoleIDs = [];
      if (filterExport?.sales_person?.length > 0) {
        filterRole = filterExport.sales_person[0]?.role?.code || 'salesman';
        filterRoleIDs = (filterExport.sales_person || []).map((a) => a.id);
      } else if (filterExport?.beauty_advisor?.length > 0) {
        filterRole =
          filterExport.beauty_advisor[0]?.role?.code || 'beauty_advisor';
        filterRoleIDs = (filterExport.beauty_advisor || []).map((a) => a.id);
      } else if (filterExport?.merchandiser?.length > 0) {
        filterRole = filterExport.merchandiser[0]?.role?.code || 'merchandiser';
        filterRoleIDs = (filterExport.merchandiser || []).map((a) => a.id);
      } else if (filterExport?.promotor?.length > 0) {
        filterRole = filterExport.promotor[0]?.role?.code || 'promotor';
        filterRoleIDs = (filterExport.promotor || []).map((a) => a.id);
      } else if (filterExport?.collector?.length > 0) {
        filterRole = filterExport.collector[0]?.role?.code || 'collector';
        filterRoleIDs = (filterExport.collector || []).map((a) => a.id);
      }

      if (
        [
          'salesman',
          'beauty_advisor',
          'merchandiser',
          'promotor',
          'collector',
        ].includes(userDetailRole)
      ) {
        filterRole = userDetailRole;
        filterRoleIDs = userDetail.id;
      }

      const payload = {
        'sales_team.id': {
          $in: filterExport.sales_team.map((a) => a.id),
        },
        visited_at: filterExport.visited_at,
      };

      payload[`${filterRole}.id`] = { $in: filterRoleIDs };

      await exportCsvApi('calender-activity', payload, {
        isDownload: false,
        message: 'Data has been export.',
      });
      setExporting(false);
      setFilterExport({
        ...filterExport,
        sales_team: [],
        sales_person: [],
        beauty_advisor: [],
        merchandiser: [],
        promotor: [],
        visited_at: {
          $gte: '',
          $lte: '',
        },
      });
      dispatch(
        showPopup({
          name: 'exportCalenderActivity',
          value: false,
        }),
      );
    } catch (error) {
      if (isIos) {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      } else {
        dispatch(
          showPopup({
            name: 'exportCalenderActivity',
            value: false,
          }),
        );
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description:
              error?.response?.data?.message || 'A system error occurred',
            isConfirm: false,
          }),
        );
      }
      setExporting(false);
    }
  };

  // if scroll activity menu list on mobile, it will change default calendar to inline calendar
  const onScrollList = (e) => {
    try {
      if (e.nativeEvent.contentOffset.y > 10) {
        setIsHideMobile(true);
      } else {
        setIsHideMobile(false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    props.navigation.addListener('focus', () => {
      resetData();
      doSearch();
    });

    return () => {
      dispatch(showPopup({ name: 'showFilter', value: false }));
      props.navigation.removeListener('focus');
    };
  }, []);

  // calender next/prev button container
  const CalendarNavigation = ({ onPrev, onNext }) => {
    return (
      <View
        style={{
          position: 'absolute',
          flexDirection: 'row',
          right: isWeb ? 40 : 15,
          marginTop: !isNative ? 10 : 17,
          zIndex: 1,
          elevation: 1,
        }}>
        <TouchableOpacity
          activeOpacity={0.6}
          style={[
            styles.buttonArrow,
            {
              marginRight: 5,
              transform: [{ rotate: isWeb ? '180deg' : '90deg' }],
            },
          ]}
          onPress={() => onPrev()}>
          <Image
            style={{ width: 13, height: 7 }}
            width={13}
            height={7}
            svg={true}
            source={require('@/assets/img/icons/arrow-down-grey.svg')}
          />
        </TouchableOpacity>
        <TouchableOpacity
          activeOpacity={0.6}
          style={[
            styles.buttonArrow,
            { transform: [{ rotate: isWeb ? '0deg' : '-90deg' }] },
          ]}
          onPress={() => onNext()}>
          <Image
            style={{ width: 13, height: 7 }}
            width={13}
            height={7}
            svg={true}
            source={require('@/assets/img/icons/arrow-down-grey.svg')}
          />
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Calendar" style={{ zIndex: 9 }}>
            <View
              style={{
                flexDirection: isWeb ? 'row' : 'column',
              }}>
              <View style={{ marginRight: 10 }}>
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 90 }}
                  title={
                    <View style={{ flexDirection: 'row' }}>
                      <Image
                        style={{ width: 12, height: 15, marginRight: 10 }}
                        width={12}
                        height={15}
                        svg={true}
                        source={require('@assets/img/icons/filter-white.svg')}
                      />
                      <SociollaText
                        type="medium"
                        style={{ fontSize: 14, color: Colors.white }}>
                        Export
                      </SociollaText>
                    </View>
                  }
                  onPress={() =>
                    setShowExportOptions((prevState) => !prevState)
                  }
                />
                {showExportOptions ? (
                  <View style={styles.addOptions}>
                    <TouchableOpacity
                      activeOpacity={0.8}
                      style={styles.addOptionsItem}
                      onPress={() => {
                        setShowExportOptions(false);
                        dispatch(
                          showPopup({
                            name: 'exportCalenderActivity',
                            value: true,
                          }),
                        );
                      }}>
                      <SociollaText>Activity</SociollaText>
                    </TouchableOpacity>
                    {hasInvoiceCollectionExportAccess ? (
                      <TouchableOpacity
                        activeOpacity={0.8}
                        style={styles.addOptionsItem}
                        onPress={() => {
                          setShowExportOptions(false);
                          dispatch(
                            showPopup({
                              name: 'exportInvoiceCollection',
                              value: true,
                            }),
                          );
                        }}>
                        <SociollaText>Invoice Collection</SociollaText>
                      </TouchableOpacity>
                    ) : (
                      false
                    )}
                  </View>
                ) : (
                  false
                )}
              </View>
              <Button
                type="primary"
                rounded={true}
                buttonStyle={{ width: 90 }}
                title={
                  <View style={{ flexDirection: 'row' }}>
                    <Image
                      style={{ width: 12, height: 15, marginRight: 10 }}
                      width={12}
                      height={15}
                      svg={true}
                      source={require('@assets/img/icons/filter-white.svg')}
                    />
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      Filter
                    </SociollaText>
                  </View>
                }
                onPress={() =>
                  dispatch(showPopup({ name: 'showFilter', value: 'calender' }))
                }
              />
            </View>
          </Layout.Title>
        </>
      ) : (
        false
      )}
      <Layout.Wrapper rounded style={{ width: '100%', maxWidth: '100%' }}>
        <View
          style={{
            position: 'relative',
            width: '100%',
            flexDirection: isWeb ? 'row' : 'column',
          }}>
          {/* Start Calendar Container */}
          <View
            onLayout={(e) => {
              try {
                if (
                  isWeb &&
                  e.nativeEvent.target.offsetHeight &&
                  !isNaN(e.nativeEvent.target.offsetHeight)
                ) {
                  setCalendarHeight(e.nativeEvent.target.offsetHeight);
                }
              } catch (error) {}
            }}
            style={[
              {
                position: 'relative',
                width: isWeb ? '65%' : '100%',
                paddingVertical: isWeb ? 20 : 10,
                paddingHorizontal: isWeb ? 20 : 20,
              },
              !isWeb && isHideMobile
                ? {
                    position: isNative ? 'absolute' : 'sticky',
                    top: 0,
                    right: 0,
                    left: 0,
                    zIndex: 1,
                    elevation: 1,
                    backgroundColor: Colors.white,
                    borderBottomWidth: 1,
                    borderBottomColor: Colors.grey0,
                  }
                : {},
            ]}>
            <CalendarNavigation
              onPrev={() => onPrev()}
              onNext={() => onNext()}
            />
            {calendar.map((item, idx) => (
              <ActivityCalendar
                firstDate={item}
                selected={calendarSelected}
                userActivity={activityObj}
                userColors={activityUser}
                isHideMobile={isHideMobile}
                onHideMobile={(val) => setIsHideMobile(val)}
                onChange={(val) => setCalendarSelected(val)}
                key={`calendar-${idx}`}
              />
            ))}
          </View>
          {/* End Calendar Container */}
          {/* Start Activity List Container */}
          <View
            style={{
              width: isWeb ? '35%' : '100%',
              // When collapsed the calendar is an absolute overlay and paddingTop
              // below already reserves its space, so maxHeight must not deduct it again.
              maxHeight: isWeb
                ? calendarHeight
                : isHideMobile
                ? totalHeight
                : totalHeight - calendarHeight,
              borderLeftColor: Colors.grey0,
              borderLeftWidth: 1,
              paddingTop: isHideMobile ? 145 : 0,
            }}>
            {loading || isCalendarLoading ? (
              <Layout.Loader />
            ) : (
              <FlatList
                data={activityData}
                ref={activityRef}
                keyExtractor={(item, index) => `${item}-${index}`}
                getItemLayout={(data, index) => ({
                  length: 50,
                  offset: 50 * index,
                  index,
                })}
                ListEmptyComponent={
                  <View
                    style={{
                      padding: 20,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <SociollaText>No Activity found</SociollaText>
                  </View>
                }
                onScrollEndDrag={(e) => onScrollList(e)}
                onScrollBeginDrag={(e) => onScrollList(e)}
                renderItem={({ item, index }) => (
                  <View style={{ width: '100%' }} key={index}>
                    <View
                      style={{
                        width: '100%',
                        backgroundColor: Colors.blue9,
                        padding: 12,
                      }}>
                      <SociollaText type="bold" style={styles.defaultTextSize}>
                        {item}
                      </SociollaText>
                    </View>
                    {activityObj[item] &&
                    Object.keys(activityObj[item]).length ? (
                      <>
                        {Object.keys(activityObj[item]).map(
                          (itemChild, childIdx) =>
                            itemChild && activityObj[item][itemChild] ? (
                              activityObj[item][itemChild].findIndex(
                                (a) => a.isEmpty,
                              ) > -1 ? (
                                <View
                                  style={{
                                    width: '100%',
                                    backgroundColor: Colors.grey3,
                                    padding: 12,
                                  }}
                                  key={`${index}-${childIdx}`}>
                                  <SociollaText style={{ fontSize: 13 }}>
                                    {itemChild}
                                  </SociollaText>
                                </View>
                              ) : (
                                <ActivityItem
                                  current={parseDate(itemChild, 'yyyy-LL-dd')}
                                  data={activityObj[item][itemChild]}
                                  userColors={activityUser}
                                  isToday={
                                    parseDate(itemChild, 'yyyy-LL-dd') ==
                                    parseDate(today, 'yyyy-LL-dd')
                                  }
                                  onPress={(val) => {
                                    if (val) {
                                      setShowActivityDetail(val);
                                    }
                                  }}
                                  key={`${index}-${childIdx}`}
                                />
                              )
                            ) : (
                              false
                            ),
                        )}
                        {loadingMore ? (
                          <Layout.Loader />
                        ) : !moreEmpty &&
                          skip <= activityTotal &&
                          activityTotal >= limit ? (
                          <TouchableOpacity
                            activeOpacity={0.8}
                            style={styles.loadMore}
                            onPress={() => doSearch(false, skip + limit)}>
                            <SociollaText
                              type="medium"
                              style={styles.defaultTextSize}>
                              Load More
                            </SociollaText>
                          </TouchableOpacity>
                        ) : (
                          false
                        )}
                      </>
                    ) : (
                      false
                    )}
                  </View>
                )}
                // ListFooterComponent={(
                //   <TouchableOpacity activeOpacity={}></TouchableOpacity>
                // )}
              />
            )}
          </View>
          {/* End Activity List Container */}
        </View>
      </Layout.Wrapper>
      <Layout.Filter
        name="calender"
        isLocalVariable
        hasFilterData={true}
        onReset={() => {
          setSelectedFilter([]);
          doSearch(true);
        }}
        onApply={() => {
          doSearch();
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem lastItem>
          <SociollaText style={{ width: '100%', marginBottom: 15 }}>
            Show Calendar
          </SociollaText>
          <ScrollContainer style={{ width: isWeb ? '45%' : '100%' }}>
            <View style={{ padding: 5 }}>
              <CheckBox
                value={''}
                checked={selectedFilter.length == 0}
                onChange={() => setSelectedFilter([])}>
                <SociollaText>All Calendar</SociollaText>
              </CheckBox>
            </View>
            {filterList.map((item, idx) => (
              <View style={{ padding: 5 }} key={idx}>
                <CheckBox
                  value={item.id}
                  checked={selectedFilter.indexOf(item.id) > -1}
                  onChange={(val) => onSetFilter(val)}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <View
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: 5,
                        marginRight: 5,
                        backgroundColor: item.color
                          ? item.color
                          : 'transparent',
                      }}></View>
                    <SociollaText>{item.name}</SociollaText>
                  </View>
                </CheckBox>
              </View>
            ))}
          </ScrollContainer>
        </Layout.FilterItem>
      </Layout.Filter>
      <ModalFull
        title={`Export Calendar`}
        show={exportCalenderActivity}
        noTranslate={true}
        onClose={() =>
          dispatch(
            showPopup({
              name: 'exportCalenderActivity',
              value: false,
            }),
          )
        }>
        <View
          style={{
            height: isWeb
              ? 'calc(100vh - 150px)'
              : dimension.height - (isAndroid ? 140 : 110),
          }}>
          <ScrollContainer contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15, height: '100%' }}>
              <Layout.FormGroup>
                <SalesTeamSearch
                  label="Sales Team"
                  value={filterExport.sales_team}
                  multiple={true}
                  customFilter={SALESTEAM_FILTER}
                  isAll
                  onChange={(value) => {
                    setFilterExport({
                      ...filterExport,
                      sales_team: value,
                      sales_person: [],
                      beauty_advisor: [],
                      merchandiser: [],
                    });
                  }}
                />
              </Layout.FormGroup>
              {![
                'salesman',
                'beauty_advisor',
                'merchandiser',
                'promotor',
                'collector',
              ].includes(userRole) ? (
                <>
                  {!filterSalesTeamChanged ? (
                    <>
                      <Layout.FormGroup>
                        <SalesPersonSearch
                          label="Sales Person"
                          value={filterExport.sales_person}
                          multiple={true}
                          customFilter={SALESPERSON_FILTER()}
                          isAll
                          onChange={(value) => {
                            setFilterExport({
                              ...filterExport,
                              sales_person: value,
                              beauty_advisor: [],
                              merchandiser: [],
                            });
                          }}
                          editable={
                            !(
                              filterExport.beauty_advisor?.length > 0 ||
                              filterExport.merchandiser?.length > 0
                            )
                          }
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SalesPersonSearch
                          label="Beauty Advisor"
                          placeholder="Select Beauty Advisor"
                          value={filterExport.beauty_advisor}
                          multiple={true}
                          customFilter={SALESPERSON_FILTER('beauty_advisor')}
                          isAll
                          onChange={(value) => {
                            setFilterExport({
                              ...filterExport,
                              beauty_advisor: value,
                              sales_person: [],
                              merchandiser: [],
                            });
                          }}
                          editable={
                            !(
                              filterExport.sales_person?.length > 0 ||
                              filterExport.merchandiser?.length > 0
                            )
                          }
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SalesPersonSearch
                          label="Merchandiser"
                          placeholder="Select Merchandiser"
                          value={filterExport.merchandiser}
                          multiple={true}
                          customFilter={SALESPERSON_FILTER('merchandiser')}
                          isAll
                          onChange={(value) => {
                            setFilterExport({
                              ...filterExport,
                              merchandiser: value,
                              beauty_advisor: [],
                              sales_person: [],
                            });
                          }}
                          editable={
                            !(
                              filterExport.sales_person?.length > 0 ||
                              filterExport.beauty_advisor?.length > 0
                            )
                          }
                        />
                      </Layout.FormGroup>
                    </>
                  ) : (
                    <Layout.FormGroup style={{ height: 64 }}>
                      <SociollaText>Loading...</SociollaText>
                    </Layout.FormGroup>
                  )}
                </>
              ) : (
                false
              )}
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Date
                </SociollaText>
                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}>
                  <DatePicker
                    label="Start Date"
                    name="visited_at"
                    style={{ width: '49%' }}
                    value={filterExport.visited_at?.$gte}
                    onChange={(value) => {
                      const val = {
                        ...filterExport.visited_at,
                        $gte: value.toISOString(),
                      };
                      setFilterExport({
                        ...filterExport,
                        visited_at: val,
                      });
                    }}
                  />
                  <DatePicker
                    label="End Date"
                    name="visited_at"
                    style={{ width: '49%' }}
                    value={filterExport.visited_at?.$lte}
                    disableBeforeDate={
                      filterExport.visited_at?.$gte
                        ? filterExport.visited_at.$gte
                        : undefined
                    }
                    disableAfterDate={
                      filterExport.visited_at?.$gte
                        ? addMonths(new Date(filterExport.visited_at.$gte), 3)
                        : undefined
                    }
                    onChange={(value) => {
                      const val = {
                        ...filterExport.visited_at,
                        $lte: value.toISOString(),
                      };
                      setFilterExport({
                        ...filterExport,
                        visited_at: val,
                      });
                    }}
                  />
                </View>
              </Layout.FormGroup>
              <Layout.FormGroup>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'center',
                    marginTop: 15,
                  }}>
                  <Button
                    rounded={true}
                    type="primary"
                    buttonStyle={{ width: 90 }}
                    disabledStyle={{ width: 90 }}
                    disabled={exporting}
                    title={
                      <SociollaText
                        type="medium"
                        style={{ fontSize: 14, color: Colors.white }}>
                        {exporting ? 'Loading...' : 'Export'}
                      </SociollaText>
                    }
                    onPress={() => onExport()}
                  />
                </View>
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollContainer>
        </View>
      </ModalFull>
      <ModalFull
        title={`Download Invoice Collection List`}
        noTranslate={true}
        show={exportInvoiceCollection}
        onClose={() =>
          dispatch(
            showPopup({
              name: 'exportInvoiceCollection',
              value: false,
            }),
          )
        }>
        <View
          style={{
            height: isWeb
              ? 'calc(100vh - 150px)'
              : dimension.height - (isAndroid ? 140 : 110),
          }}>
          <ScrollContainer contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15, height: '100%' }}>
              {!['salesman'].includes(userRole) ? (
                <>
                  <Layout.FormGroup>
                    <SalesTeamSearch
                      label="Sales Team"
                      value={filterInvoiceExport.sales_team}
                      multiple={true}
                      customFilter={SALESTEAM_FILTER}
                      isAll
                      onChange={(value) => {
                        setFilterInvoiceExport({
                          ...filterInvoiceExport,
                          sales_team: value,
                          salesman: null,
                        });
                      }}
                    />
                  </Layout.FormGroup>
                  <Layout.FormGroup>
                    <SalesPersonSearch
                      label="Sales Person"
                      value={filterInvoiceExport.salesman}
                      multiple={false}
                      customFilter={INVOICE_SALESPERSON_FILTER}
                      onChange={(value) => {
                        setFilterInvoiceExport({
                          ...filterInvoiceExport,
                          salesman: value,
                        });
                      }}
                    />
                  </Layout.FormGroup>
                </>
              ) : (
                false
              )}
              <Layout.FormGroup>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Date
                </SociollaText>
                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}>
                  <DatePicker
                    label="Start Date"
                    name="visited_at"
                    style={{ width: '49%' }}
                    value={filterInvoiceExport.visited_at?.$gte}
                    onChange={(value) => {
                      const val = {
                        ...filterInvoiceExport.visited_at,
                        $gte: value.toISOString(),
                      };
                      setFilterInvoiceExport({
                        ...filterInvoiceExport,
                        visited_at: val,
                      });
                    }}
                  />
                  <DatePicker
                    label="End Date"
                    name="visited_at"
                    style={{ width: '49%' }}
                    value={filterInvoiceExport.visited_at?.$lte}
                    disableBeforeDate={filterInvoiceExport.visited_at?.$gte}
                    disableAfterDate={
                      filterInvoiceExport.visited_at?.$gte
                        ? addMonths(
                            new Date(filterInvoiceExport.visited_at.$gte),
                            3,
                          )
                        : undefined
                    }
                    onChange={(value) => {
                      const val = {
                        ...filterInvoiceExport.visited_at,
                        $lte: value.toISOString(),
                      };
                      setFilterInvoiceExport({
                        ...filterInvoiceExport,
                        visited_at: val,
                      });
                    }}
                  />
                </View>
              </Layout.FormGroup>
              <Layout.FormGroup lastItem>
                <View style={styles.invoiceCollectionNote}>
                  <SociollaText style={styles.invoiceCollectionNoteText}>
                    Only open invoices for stores assigned to the selected
                    salesman within the date range will be included.
                  </SociollaText>
                </View>
              </Layout.FormGroup>
              <Layout.FormGroup>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'center',
                    marginTop: 15,
                  }}>
                  <Button
                    rounded={true}
                    type="primary"
                    buttonStyle={{ width: 150 }}
                    disabledStyle={{ width: 150 }}
                    disabled={exportingInvoiceCollection}
                    title={
                      <SociollaText
                        type="medium"
                        style={{ fontSize: 14, color: Colors.white }}>
                        {exportingInvoiceCollection
                          ? 'Loading...'
                          : 'Download PDF'}
                      </SociollaText>
                    }
                    onPress={() => onExportInvoiceCollection()}
                  />
                </View>
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollContainer>
        </View>
      </ModalFull>
      <SalesActivityDetail
        id={showActivityDetail}
        show={showActivityDetail ? true : false}
        onClose={() => setShowActivityDetail('')}
      />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  buttonArrow: {
    width: 30,
    height: 30,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
    borderColor: Colors.grey0,
    borderWidth: 1,
  },
  loadMore: {
    padding: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  defaultTextSize: { fontSize: 13 },
  addOptions: {
    position: 'absolute',
    width: 180,
    top: 50,
    left: -60,
    zIndex: 9,
    elevation: 1,
    backgroundColor: Colors.white,
    borderRadius: 5,
    borderColor: Colors.grey0,
    borderWidth: 1,
    ...(!isWeb
      ? {
          position: 'absolute',
          top: 0,
          left: 100,
        }
      : {}),
  },
  addOptionsItem: {
    paddingVertical: 13,
    paddingHorizontal: 15,
    ...(!isWeb
      ? {
          borderBottomColor: Colors.grey0,
          borderBottomWidth: 1,
        }
      : {}),
  },
  invoiceCollectionNote: {
    backgroundColor: '#FEF3C7',
    borderColor: '#F5D98B',
    borderWidth: 1,
    borderRadius: 8,
    padding: 15,
  },
  invoiceCollectionNoteText: {
    fontSize: 13,
    color: '#92400E',
    lineHeight: 18,
  },
});

export default CalendarScreen;
